import React, { Fragment, useEffect, useState } from 'react';
import { useParams } from "react-router-dom";
import axios from 'axios';
import Loading from 'react-fullscreen-loading';
import { loadStripe } from '@stripe/stripe-js';

import './index.scss';
import API_ROUTES from "../../config/api";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const DOCUMENT_TYPES = {
  passport: {
    label: 'Passport',
    description: 'Please have your passport ready. You will need to take a photo of the photo page.',
    icon: (
      <svg className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
      </svg>
    ),
  },
  driving_license: {
    label: 'Driving License',
    description: 'Please have your driving license ready. You will need to take photos of both sides.',
    icon: (
      <svg className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
  },
};

function App() {
  const [director, setDirector] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);

  // Track verification status for each document type
  const [passportVerified, setPassportVerified] = useState(false);
  const [drivingLicenseVerified, setDrivingLicenseVerified] = useState(false);
  const [currentStep, setCurrentStep] = useState('passport'); // 'passport' | 'driving_license' | 'complete'

  let { dealID, contactID } = useParams();

  useEffect(() => {
    const fetchDirector = async () => {
      const statusResponse = await axios.get(
        API_ROUTES.onboarding['application:director:fetch'](dealID, contactID)
      );

      const directorData = statusResponse.data.data;
      setDirector(directorData);

      // Check if documents are already verified
      if (directorData.passport_verified) {
        setPassportVerified(true);
        setCurrentStep('driving_license');
      }
      if (directorData.driving_license_verified) {
        setDrivingLicenseVerified(true);
      }
      if (directorData.passport_verified && directorData.driving_license_verified) {
        setCurrentStep('complete');
      }
    };

    fetchDirector();
  }, [dealID, contactID]);

  const startVerification = async (documentType) => {
    setVerifying(true);
    setError(null);

    try {
      const stripe = await stripePromise;

      const sessionResponse = await axios.post(
        API_ROUTES.onboarding['application:director:verification-session'](dealID, contactID),
        {
          deal_id: dealID,
          contact_id: contactID,
          document_type: documentType,
        }
      );

      const { client_secret } = sessionResponse.data.data;

      const { error: stripeError } = await stripe.verifyIdentity(client_secret);

      if (stripeError) {
        setError(stripeError.message);
        setVerifying(false);
      } else {
        // Mark document as verified and move to next step
        if (documentType === 'passport') {
          setPassportVerified(true);
          setCurrentStep('driving_license');
        } else if (documentType === 'driving_license') {
          setDrivingLicenseVerified(true);
          setCurrentStep('complete');
        }
        setVerifying(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred during verification');
      setVerifying(false);
    }
  };

  if (!director) {
    return (
      <Loading loading background="#670199" loaderColor="#fff" />
    );
  }

  // All verifications complete
  if (currentStep === 'complete' || (passportVerified && drivingLicenseVerified)) {
    return (
      <Fragment>
        <div className="text-center py-12">
          <div className="mb-6">
            <svg className="mx-auto h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-4">Identity Verification Complete</h1>
          <p className="text-gray-600 mb-6">
            Thank you for completing your identity verification. Both your passport and driving license have been submitted successfully and are now being reviewed.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            You will receive an email notification once your verification has been processed. This typically takes 1-2 business days.
          </p>
          <a
            href={`/${dealID}/status`}
            className="bg-purple-800 hover:bg-purple-900 text-white font-semibold rounded-md py-2 px-6"
          >
            View Application Status
          </a>
        </div>
      </Fragment>
    );
  }

  const currentDocument = DOCUMENT_TYPES[currentStep];

  return (
    <Fragment>
      <div>
        <h1 className="text-2xl font-semibold">Identity Verification</h1>
        <p className='text-sm mb-6'>
          As part of the onboarding process for your organisation to Felloh, we require you to verify your identity using two documents: a passport and a driving license.
        </p>

        {/* Director info */}
        <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
          <div className="mb-2">
            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 text-felloh">
              Name
            </label>
            <p>{director.firstname} {director.lastname}</p>
          </div>

          <div className="mb-2">
            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 text-felloh">
              Email Address
            </label>
            <p>{director.posc_email}</p>
          </div>

          <div className="sm:col-span-2 mb-2">
            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 text-felloh">
              Address
            </label>
            <p>{[director.address, director.county, director.city, director.zip, director.country]
              .filter(Boolean)
              .join(', ')
              .split(', ')
              .filter(part => part && part !== 'undefined')
              .join(', ')}</p>
          </div>
        </div>

        {/* Current document verification card */}
        <div className="border border-gray-200 rounded-lg p-6 mb-6 mt-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              {currentDocument.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Step {currentStep === 'passport' ? '1' : '2'}: Verify your {currentDocument.label}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {currentDocument.description}
              </p>
            </div>
          </div>
          <div className="mt-6">
            <button
              type="button"
              onClick={() => startVerification(currentStep)}
              disabled={verifying}
              className="bg-purple-800 disabled:bg-purple-200 hover:bg-purple-900 text-white font-semibold rounded-md py-3 px-4 w-full"
            >
              {verifying ? 'Opening verification...' : `Verify ${currentDocument.label}`}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Document checklist */}
        <div className="mt-8 border-t pt-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Document Checklist</h4>
          <ul className="space-y-2">
            <li className="flex items-center text-sm">
              {passportVerified ? (
                <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-gray-300 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              <span className={passportVerified ? 'text-green-700' : 'text-gray-600'}>
                Passport {passportVerified ? '- Verified' : '- Pending'}
              </span>
            </li>
            <li className="flex items-center text-sm">
              {drivingLicenseVerified ? (
                <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-gray-300 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              <span className={drivingLicenseVerified ? 'text-green-700' : 'text-gray-600'}>
                Driving License {drivingLicenseVerified ? '- Verified' : '- Pending'}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </Fragment>
  );
}

export default App;
